import { Link, useLocation } from "react-router-dom";
import { useSidebar } from "./SidebarContext";
import { useEffect, useMemo, useRef, useState, type ComponentType } from "react";
import {
  Activity,
  BarChart3,
  Bell,
  Building2,
  CalendarDays,
  CarFront,
  ChevronDown,
  CreditCard,
  Download,
  FileText,
  LayoutDashboard,
  LifeBuoy,
  Mail,
  MapPin,
  Menu,
  QrCode,
  ReceiptText,
  Scale,
  Settings,
  Shield,
  Upload,
  Users,
  Wallet,
  Wrench,
  X,
} from "lucide-react";

import { cn } from "../utils";
import { useAuth } from "./AuthProvider";
import { useEffectiveAccess } from "../hooks/useEffectiveAccess";
import logoFull from "../assets/logo-full.svg";
import logoIcon from "../assets/logo-icon.svg";

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
  appScope?: "business" | "platform-admin";
}

type SidebarItem = {
  name: string;
  href: string;
  icon: ComponentType<{ className?: string }>;
  phase2?: boolean; // hidden from nav until Phase 2; pages still accessible by URL
  module?: string; // module name for access control; items without a module are always visible
};

type SidebarSection = {
  title: string;
  icon: ComponentType<{ className?: string }>;
  items: SidebarItem[];
};

const businessSections: SidebarSection[] = [
  {
    title: "Overview",
    icon: LayoutDashboard,
    items: [
      { name: "Dashboard", href: "/", icon: LayoutDashboard },
      { name: "Notifications", href: "/notifications", icon: Bell },
    ],
  },
  {
    title: "Operations",
    icon: CalendarDays,
    items: [
      { name: "Bookings", href: "/bookings", icon: CalendarDays, module: "bookings" },
      { name: "Vehicles", href: "/vehicles", icon: CarFront, module: "vehicles" },
      { name: "Customers", href: "/customers", icon: Users, module: "customers" },
      { name: "Payments", href: "/payments", icon: CreditCard, module: "payments" },
      { name: "Maintenance", href: "/maintenance", icon: Wrench, module: "maintenance" },
    ],
  },
  {
    title: "Management",
    icon: Settings,
    items: [
      { name: "Agreements", href: "/agreements", icon: FileText, module: "documents" },
      // Settings removed from sidebar - access via profile button only
    ],
  },
  {
    title: "Tools",
    icon: Wrench,
    items: [
      { name: "QR Access", href: "/qr-access", icon: QrCode, module: "qr" },
    ],
  },
];

const platformSections: SidebarSection[] = [
  {
    title: "Platform Overview",
    icon: LayoutDashboard,
    items: [
      { name: "Admin Dashboard", href: "/console", icon: LayoutDashboard },
      { name: "Platform Analytics", href: "/console/analytics", icon: BarChart3 },
      { name: "Platform Health", href: "/console/health", icon: Activity },
      { name: "Activity Logs", href: "/console/logs", icon: Activity },
    ],
  },
  {
    title: "Business Control",
    icon: Building2,
    items: [
      { name: "Business Management", href: "/console/businesses", icon: Building2 },
      { name: "Subscription Plans", href: "/console/plans", icon: ReceiptText },
      { name: "Billing & Payments", href: "/console/billing", icon: CreditCard },
      { name: "Finance & Costs", href: "/console/finance", icon: Wallet },
    ],
  },
  {
    title: "Users & Access",
    icon: Users,
    items: [
      { name: "All Users", href: "/console/users", icon: Users },
      { name: "Role Management", href: "/console/role-management", icon: Shield },
      { name: "Notifications", href: "/console/notifications", icon: Bell },
    ],
  },
  {
    title: "Content & Support",
    icon: Settings,
    items: [
      { name: "Support Tickets", href: "/console/support", icon: LifeBuoy },
      { name: "CMS & Blog", href: "/console/cms", icon: FileText },
      { name: "Legal Pages", href: "/console/legal-pages", icon: Scale },
      { name: "Testimonials", href: "/console/testimonials", icon: ReceiptText },
      { name: "Email Templates", href: "/console/email-templates", icon: Mail },
      { name: "Settings", href: "/console/settings", icon: Settings },
    ],
  },
];

export default function Sidebar({ isOpen = true, onClose = () => undefined, appScope = "business" }: SidebarProps) {
  const { collapsed, toggleCollapsed } = useSidebar();
  const pathname = useLocation().pathname;
  const { user, signOut, canAccessRoute } = useAuth();
  const { canAccess } = useEffectiveAccess();
  const sectionSource = appScope === "platform-admin" ? platformSections : businessSections;
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(sectionSource.map((section) => [section.title, true])),
  );

  // Close sidebar on route change (mobile only).
  // We track the previous pathname to avoid firing on initial mount.
  const prevPathnameRef = useRef(pathname);
  useEffect(() => {
    if (prevPathnameRef.current !== pathname) {
      prevPathnameRef.current = pathname;
      onClose();
    }
  }, [pathname, onClose]);

  const visibleSections = useMemo(
    () =>
      sectionSource
        .map((section) => ({
          ...section,
          items: section.items
            .filter((item) => !item.phase2) // Phase 2 items hidden until ready
            .filter((item) => canAccessRoute(item.href))
            .filter((item) => {
              // For business app: hide items whose module the user cannot access
              // Items without a module property are always visible (Dashboard, Notifications, etc.)
              if (appScope !== "business") return true;
              if (!item.module) return true;
              return canAccess(item.module);
            }),
        }))
        .filter((section) => section.items.length > 0),
    [canAccessRoute, canAccess, sectionSource, appScope],
  );

  return (
    <>
      <aside
        className={cn(
          "fixed left-0 top-0 z-40 flex h-screen flex-col border-r border-white/10 bg-sidebar py-6 transition-[width,transform] duration-200 lg:z-20 lg:translate-x-0",
          isOpen ? "translate-x-0" : "-translate-x-full",
          collapsed ? "w-[72px] px-2" : "w-72 px-4",
          collapsed && "sidebar-collapsed",
        )}
      >
        <div className={cn("mb-8 flex items-center px-2", collapsed ? "justify-center" : "justify-between")}>
          {collapsed ? (
            <button
              type="button"
              onClick={toggleCollapsed}
              className="flex h-10 w-10 items-center justify-center rounded-lg hover:bg-white/10 transition-colors"
              title="Expand sidebar"
            >
              <img src={logoIcon} alt="RentOS" className="h-8 w-8" />
            </button>
          ) : (
            <Link to={appScope === "platform-admin" ? "/console" : "/"}
              className="flex items-center"
            >
              <img src={logoFull} alt="RentOS" className="h-9 w-auto" />
            </Link>
          )}
          {!collapsed && (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={toggleCollapsed}
                className="hidden h-9 w-9 items-center justify-center rounded-lg text-slate-400 hover:bg-white/10 hover:text-white transition-colors lg:flex"
              >
                <Menu className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex h-10 w-10 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground lg:hidden"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-1 custom-scrollbar">
          <nav className={cn("space-y-4", collapsed && "space-y-1")}>
            {visibleSections.map((section) => {
              const expanded = expandedSections[section.title] ?? true;
              const sectionActive = section.items.some(
                (item) => pathname === item.href || (item.href !== "/" && pathname?.startsWith(item.href)),
              );

              return (
                <div key={section.title} className={cn("rounded-xl", collapsed ? "p-0" : "p-1.5")}>
                  {!collapsed && (
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedSections((current) => ({ ...current, [section.title]: !expanded }))
                      }
                    className={cn(
                      "flex w-full items-center justify-between rounded-xl px-3 py-2 text-left transition-colors",
                      sectionActive
                          ? "bg-sidebar-active-bg text-sidebar-active-fg"
                          : "text-sidebar-fg hover:bg-sidebar-active-bg/50 hover:text-sidebar-active-fg",
                    )}
                    >
                      <span className="flex items-center gap-2 text-sm font-semibold">
                        <section.icon className="h-4 w-4" />
                        <span className="sidebar-label">{section.title}</span>
                      </span>
                      <ChevronDown className={cn("h-4 w-4 transition-transform", expanded ? "rotate-180" : "")} />
                    </button>
                  )}

                  {(collapsed || expanded) ? (
                    <div className={cn("space-y-1", !collapsed && "mt-2")}>
                      {section.items.map((item) => {
                        const isActive =
                          pathname === item.href || (item.href !== "/" && pathname?.startsWith(item.href));
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            onClick={() => {
                              if (window.innerWidth < 1024) {
                                onClose();
                              }
                            }}
                          className={cn(
                              "sidebar-item group relative flex items-center rounded-xl border text-sm font-medium transition-all duration-200",
                              collapsed
                                ? "justify-center p-2.5 min-h-[40px]"
                                : "gap-3 px-3 py-3 min-h-[44px]",
                              isActive
                                ? "border-sidebar-active-fg/20 bg-sidebar-active-bg text-sidebar-active-fg shadow-sm"
                                : "border-transparent text-sidebar-fg hover:bg-sidebar-active-bg/50 hover:text-sidebar-active-fg",
                            )}
                            title={collapsed ? item.name : undefined}
                          >
                            {isActive && !collapsed ? (
                              <div className="absolute left-0 top-1/2 -mt-2 h-4 w-1 rounded-r-full bg-primary" />
                            ) : null}
                            <item.icon
                              className={cn(
                                "h-4 w-4 shrink-0 transition-colors",
                                isActive ? "text-primary" : "text-muted-foreground group-hover:text-sidebar-active-fg",
                              )}
                            />
                            {!collapsed && <span className="sidebar-label">{item.name}</span>}
                          </Link>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </nav>
        </div>

        {/* Bottom profile strip */}
        <div className={cn("mt-4 border-t border-white/10 pt-4", collapsed ? "px-1" : "px-2")}>
          <Link to={appScope === "platform-admin" ? "/console/profile" : "/profile"}
            className={cn(
              "flex items-center rounded-xl text-sm font-medium text-sidebar-fg hover:bg-sidebar-active-bg/50 hover:text-sidebar-active-fg transition-colors",
              collapsed ? "justify-center p-2.5" : "gap-3 px-3 py-2.5",
            )}
            title={collapsed ? (user?.name ?? "Profile") : undefined}
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/20 text-xs font-bold text-sidebar-active-fg">
              {user?.name?.charAt(0)?.toUpperCase() ?? "A"}
            </div>
            {!collapsed && <span className="sidebar-label truncate">{user?.name ?? "Admin"}</span>}
          </Link>
          {/* Sign out removed — available from profile dropdown in topbar */}
          {!collapsed && appScope === "business" && (
            <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 px-3 pt-2 border-t border-white/5">
              <a href="https://rentos.tech/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-[10px] text-sidebar-fg/50 hover:text-sidebar-fg/80 transition-colors">Privacy</a>
              <a href="https://rentos.tech/legal/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-[10px] text-sidebar-fg/50 hover:text-sidebar-fg/80 transition-colors">Terms</a>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
